import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MapContainer, TileLayer, Polyline, CircleMarker, Marker, Popup, Tooltip } from "react-leaflet";
import MapBaseLayers from "../components/MapBaseLayers";
import L from "leaflet";
import api from "../lib/api";
import { PageHeader } from "../components/Table";
import { Play, Pause, Flag, Warning, ShieldWarning, Info } from "@phosphor-icons/react";

const CAT_COLOR = { critical: "#FF3B30", warning: "#FF9500", operational: "#5AC8FA" };
const isLight = () => document.documentElement.classList.contains("light");
const tileUrl = () => isLight()
  ? "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
  : "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png";

function curIcon(color) {
  return L.divIcon({ className: "veh-marker", iconSize: [22, 22], iconAnchor: [11, 11],
    html: `<div style="width:20px;height:20px;background:${color};border:3px solid #fff;border-radius:50%;box-shadow:0 0 0 3px rgba(124,58,183,0.5)"></div>` });
}

function Metric({ label, value }) {
  return (
    <div className="bg-card border border-border rounded-sm px-3 py-2">
      <div className="label-caps text-[9px]">{label}</div>
      <div className="font-display font-bold text-base">{value}</div>
    </div>
  );
}

export default function History() {
  const [params] = useSearchParams();
  const [vehicles, setVehicles] = useState([]);
  const [vid, setVid] = useState(params.get("v") || "");
  const [data, setData] = useState(null);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(2);
  const [selEvent, setSelEvent] = useState(null);
  const [place, setPlace] = useState("");

  useEffect(() => {
    api.get("/vehicles").then((r) => { setVehicles(r.data.vehicles); if (!vid && r.data.vehicles[0]) setVid(r.data.vehicles[0].id); });
  }, []);
  useEffect(() => {
    if (!vid) return;
    setData(null); setIdx(0); setPlaying(false); setSelEvent(null);
    api.get(`/vehicles/${vid}/history?limit=1500`).then((r) => setData(r.data));
  }, [vid]);
  useEffect(() => {
    if (!playing || !data) return;
    const t = setInterval(() => setIdx((i) => (i >= data.points.length - 1 ? (setPlaying(false), i) : i + 1)), 350 / speed);
    return () => clearInterval(t);
  }, [playing, data, speed]);

  const points = data?.points || [];
  const trail = points.slice(0, idx + 1).map((p) => [p.lat, p.lng]);
  const cur = points[idx];

  useEffect(() => {
    if (!cur) { setPlace(""); return; }
    let cancel = false;
    api.get(`/geocode?lat=${cur.lat}&lng=${cur.lng}`)
      .then((r) => { if (!cancel) setPlace(r.data.place); }).catch(() => {});
    return () => { cancel = true; };
  }, [cur?.lat, cur?.lng]);
  const s = data?.summary;
  const startT = s?.start ? new Date(s.start) : null;
  const endT = s?.end ? new Date(s.end) : null;
  const span = startT && endT ? Math.max(endT - startT, 1) : 1;
  const posPct = (t) => startT ? Math.min(100, Math.max(0, (new Date(t) - startT) / span * 100)) : 0;

  return (
    <div className="p-5 space-y-4">
      <PageHeader title="History Playback"
        subtitle={data ? `${data.vehicle.vehicle_number} · ${data.vehicle.driver_name} · ${data.vehicle.vehicle_type}` : "Detailed journey replay with events & data-quality flags"} />

      <div className="flex flex-wrap items-center gap-3">
        <select data-testid="history-vehicle-select" value={vid} onChange={(e) => setVid(e.target.value)}
          className="bg-card border border-border rounded-sm px-3 py-2 text-sm min-w-[180px]">
          {vehicles.map((v) => <option key={v.id} value={v.id}>{v.vehicle_number}</option>)}
        </select>
        <button data-testid="history-play" onClick={() => setPlaying((p) => !p)}
          className="flex items-center gap-1.5 bg-primary text-white text-sm font-semibold px-4 py-2 rounded-sm">
          {playing ? <Pause size={16} weight="fill" /> : <Play size={16} weight="fill" />}{playing ? "Pause" : "Play"}
        </button>
        <div className="flex gap-1">{[1, 2, 5, 10].map((sp) => (
          <button key={sp} onClick={() => setSpeed(sp)} className={`text-xs font-bold px-2.5 py-2 rounded-sm border ${speed === sp ? "border-primary bg-primary/15" : "border-border text-muted-foreground"}`}>{sp}×</button>
        ))}</div>
        {cur && <span className="text-xs text-muted-foreground font-mono ml-auto">{new Date(cur.timestamp).toLocaleString()} · {cur.speed} km/h · {idx + 1}/{points.length}</span>}
      </div>

      {/* Summary */}
      {s && (
        <div className="grid grid-cols-3 md:grid-cols-6 lg:grid-cols-9 gap-2" data-testid="playback-summary">
          <Metric label="Start" value={startT ? startT.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"} />
          <Metric label="End" value={endT ? endT.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"} />
          <Metric label="Distance" value={`${s.total_distance} km`} />
          <Metric label="Running" value={`${Math.floor(s.running_min / 60)}h ${s.running_min % 60}m`} />
          <Metric label="Idle" value={`${s.idle_min}m`} />
          <Metric label="Stop" value={`${Math.floor(s.stop_min / 60)}h ${s.stop_min % 60}m`} />
          <Metric label="Max Spd" value={`${s.max_speed}`} />
          <Metric label="Avg Spd" value={`${s.avg_speed}`} />
          <Metric label="Flags" value={s.flag_count} />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="lg:col-span-3 border border-border rounded-sm overflow-hidden h-[460px]">
          <MapContainer center={cur ? [cur.lat, cur.lng] : [21.5, 78.9]} zoom={cur ? 12 : 5} style={{ height: "100%", width: "100%" }} scrollWheelZoom preferCanvas>
            <MapBaseLayers />
            {points.length > 1 && <Polyline positions={points.map((p) => [p.lat, p.lng])} pathOptions={{ color: "#7C3AED", weight: 2, opacity: 0.5 }} />}
            {trail.length > 1 && <Polyline positions={trail} pathOptions={{ color: "#7C3AED", weight: 4 }} />}
            {points[0] && <CircleMarker center={[points[0].lat, points[0].lng]} radius={6} pathOptions={{ color: "#34C759", fillColor: "#34C759", fillOpacity: 1 }}><Tooltip>Start</Tooltip></CircleMarker>}
            {points.length > 1 && <CircleMarker center={[points[points.length - 1].lat, points[points.length - 1].lng]} radius={6} pathOptions={{ color: "#FF3B30", fillColor: "#FF3B30", fillOpacity: 1 }}><Tooltip>End</Tooltip></CircleMarker>}
            {(data?.events || []).filter((e) => e.lat).map((e, i) => (
              <CircleMarker key={i} center={[e.lat, e.lng]} radius={selEvent === e ? 8 : 5}
                pathOptions={{ color: CAT_COLOR[e.category], fillColor: CAT_COLOR[e.category], fillOpacity: 0.9 }}
                eventHandlers={{ click: () => setSelEvent(e) }}>
                <Tooltip>{e.type}</Tooltip>
              </CircleMarker>
            ))}
            {cur && <Marker position={[cur.lat, cur.lng]} icon={curIcon(cur.ignition ? "#34C759" : "#FF9500")}>
              <Popup>
                <div className="text-xs leading-relaxed">
                  <div className="font-bold text-sm">{data.vehicle.vehicle_number}</div>
                  <div>Driver: {data.vehicle.driver_name}</div>
                  <div>{new Date(cur.timestamp).toLocaleString()}</div>
                  <div>Location: {place || cur.location}</div>
                  <div>Speed: {cur.speed} km/h · Dir {cur.heading}°</div>
                  <div>Ignition: {cur.ignition ? "ON" : "OFF"} · GPS: {cur.gps_valid ? "Valid" : "No fix"}</div>
                  <div>Odometer: {cur.odometer} km · {cur.satellites} sats · {cur.voltage} V</div>
                </div>
              </Popup>
            </Marker>}
          </MapContainer>
        </div>

        {/* Side: events + flags */}
        <div className="space-y-3">
          {selEvent && (
            <div className="bg-card border border-border rounded-sm p-3" data-testid="event-detail" style={{ borderColor: CAT_COLOR[selEvent.category] }}>
              <div className="font-display font-bold" style={{ color: CAT_COLOR[selEvent.category] }}>{selEvent.type}</div>
              <div className="text-xs text-muted-foreground">{new Date(selEvent.time).toLocaleString()}</div>
              {selEvent.speed != null && <div className="text-xs mt-1">Speed {selEvent.speed} · Allowed {selEvent.allowed} · Excess {selEvent.excess} km/h</div>}
              {selEvent.before != null && <div className="text-xs mt-1">{selEvent.before} → {selEvent.after} km/h</div>}
              {selEvent.message && <div className="text-xs mt-1">{selEvent.message}</div>}
            </div>
          )}
          <div className="bg-card border border-border rounded-sm p-3 h-[190px] overflow-y-auto" data-testid="events-list">
            <div className="label-caps mb-2">Events ({data?.events?.length || 0})</div>
            {(data?.events || []).map((e, i) => (
              <button key={i} onClick={() => setSelEvent(e)} className="w-full text-left flex items-center gap-2 py-1 text-xs hover:bg-white/5 rounded-sm px-1">
                <span className="status-dot shrink-0" style={{ background: CAT_COLOR[e.category] }} />
                <span className="font-semibold">{e.type}</span>
                <span className="text-muted-foreground ml-auto">{new Date(e.time).toLocaleTimeString()}</span>
              </button>
            ))}
          </div>
          <div className="bg-card border border-border rounded-sm p-3 h-[150px] overflow-y-auto" data-testid="flags-list">
            <div className="label-caps mb-2 flex items-center gap-1"><ShieldWarning size={12} /> Data Quality Flags ({data?.flags?.length || 0})</div>
            {(data?.flags || []).length === 0 && <div className="text-xs text-muted-foreground">No data-quality issues</div>}
            {(data?.flags || []).map((f, i) => (
              <div key={i} className="flex items-center gap-2 py-0.5 text-xs">
                <span className="font-mono text-[#FF9500]">{f.type}</span>
                <span className="text-muted-foreground">{f.detail}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Timeline */}
      {points.length > 0 && (
        <div className="bg-card border border-border rounded-sm p-4" data-testid="playback-timeline">
          <div className="flex items-center justify-between mb-2 text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><Flag size={12} weight="fill" color="#34C759" /> Start</span>
            <span className="flex gap-3">
              <span className="flex items-center gap-1"><span className="status-dot" style={{ background: "#FF3B30" }} /> Critical</span>
              <span className="flex items-center gap-1"><span className="status-dot" style={{ background: "#FF9500" }} /> Warning</span>
              <span className="flex items-center gap-1"><span className="status-dot" style={{ background: "#5AC8FA" }} /> Operational</span>
            </span>
            <span className="flex items-center gap-1">End <Flag size={12} weight="fill" color="#FF3B30" /></span>
          </div>
          <div className="relative h-8 bg-secondary rounded-sm">
            <div className="absolute top-1/2 left-0 h-1 bg-primary rounded" style={{ width: `${(idx / Math.max(points.length - 1, 1)) * 100}%`, transform: "translateY(-50%)" }} />
            <input type="range" min={0} max={Math.max(points.length - 1, 0)} value={idx}
              onChange={(e) => { setIdx(Number(e.target.value)); setPlaying(false); }}
              data-testid="timeline-scrubber" className="absolute inset-0 w-full opacity-0 cursor-pointer" style={{ zIndex: 5 }} />
            {(data?.events || []).map((e, i) => (
              <button key={i} onClick={() => setSelEvent(e)} title={e.type}
                data-testid={`timeline-dot-${i}`}
                className="absolute -translate-x-1/2" style={{ left: `${posPct(e.time)}%`, top: "50%", transform: "translate(-50%,-50%)", zIndex: 20 }}>
                <span className="status-dot" style={{ background: CAT_COLOR[e.category], width: selEvent === e ? 12 : 9, height: selEvent === e ? 12 : 9, display: "block", border: "1px solid #fff" }} />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
